/**
 * The only module allowed to construct an Intl.DateTimeFormat or touch a Date.
 *
 * Everything displayed is FACILITY time. Somebody in Madrid booking for their
 * cousin in Montevideo must see 09:20, not 14:20. Uruguay has had no daylight
 * saving since 2015, but nothing here hardcodes -03:00 -- Intl with the IANA
 * name is the same amount of code and stays correct if that ever changes.
 */

export const FACILITY_TZ = 'America/Montevideo'

/**
 * Displayed to a person. Uruguayan Spanish.
 *
 * NOT the same thing as the locales below it: `todayAtFacility` and
 * `nowAtFacility` produce KEYS, not prose, and must never use this.
 */
const DISPLAY_LOCALE = 'es-UY'

/**
 * Today at the facility, as 'YYYY-MM-DD'.
 *
 * 'en-CA' is the trick: its short date format is already ISO order.
 *
 * DO NOT "TRANSLATE" THIS TO es-UY. The output is a KEY, not something anybody
 * reads -- it goes into router search params, availability queries and every
 * date comparison in lib/month.ts. es-UY would yield '21/8/2026' and break all
 * of that silently. Same for nowAtFacility below, which is HH:MM only.
 */
export function todayAtFacility(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: FACILITY_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now)
}

/**
 * The current wall clock at the facility, 'HH:MM'.
 *
 * hourCycle 'h23', NOT hour12:false -- the latter renders midnight as '24:00'
 * in some engines, which would sort after '20:00' and quietly break every
 * comparison built on these strings.
 */
export function nowAtFacility(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: FACILITY_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(now)
}

/**
 * Format a PLAIN DATE ('YYYY-MM-DD').
 *
 * Parsed as UTC midnight AND formatted with timeZone 'UTC'. That pairing is not
 * optional: a plain date has no instant, and formatting '2026-08-20T00:00:00Z'
 * in Montevideo yields the 19th, so every customer would see the wrong weekday.
 */
function plainDateFormat(date: string, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(DISPLAY_LOCALE, { timeZone: 'UTC', ...options }).format(
    new Date(`${date}T00:00:00Z`),
  )
}

/**
 * 'viernes, 21 de agosto'.
 *
 * The backend produces this same shape by hand in app/core/dates.py, because
 * Python's %A/%B read a C library that is POSIX in the container. The email
 * and the screen show the same date side by side, so if you change the shape
 * here, change it there too.
 */
export const formatDayLong = (date: string): string =>
  plainDateFormat(date, { weekday: 'long', day: 'numeric', month: 'long' })

export const formatWeekday = (date: string): string => plainDateFormat(date, { weekday: 'short' })

export const dayOfMonth = (date: string): string => date.slice(8)

/** Plain-date arithmetic in UTC, so a DST jump can never shift it. */
export function addDays(date: string, days: number): string {
  const shifted = new Date(`${date}T00:00:00Z`)
  shifted.setUTCDate(shifted.getUTCDate() + days)
  return shifted.toISOString().slice(0, 10)
}

/**
 * The only real conversion in the app: a stored booking's `starts_at` comes back
 * from Postgres as a timestamptz, i.e. an actual instant.
 */
export function formatInstant(iso: string): string {
  return new Intl.DateTimeFormat(DISPLAY_LOCALE, {
    timeZone: FACILITY_TZ,
    dateStyle: 'long',
    timeStyle: 'short',
    // WITHOUT THIS you get '2:00 p. m.'. CLDR has es-UY on a 12-hour clock,
    // which is not how anybody here writes a time and disagrees with every
    // other clock in the app.
    hourCycle: 'h23',
  }).format(new Date(iso))
}

/**
 * 'vie 21, 14:00' -- a booking, compactly, for a list where the year and the
 * month are already obvious from context.
 *
 * Lives here rather than at its call site because this module opens by
 * claiming to be the only place allowed to construct an Intl.DateTimeFormat,
 * and that claim had already drifted once.
 */
export function formatShortInstant(iso: string): string {
  return new Intl.DateTimeFormat(DISPLAY_LOCALE, {
    timeZone: FACILITY_TZ,
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(iso))
}

/** '18:42' -- when something happened, for a list already grouped by day. */
export function formatClock(iso: string): string {
  return new Intl.DateTimeFormat(DISPLAY_LOCALE, {
    timeZone: FACILITY_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(iso))
}

/** The facility-local calendar day an instant falls on, 'YYYY-MM-DD' -- so a
 *  change at 23:30 in Montevideo is grouped under that day, not tomorrow's
 *  UTC one. */
export const localDateOf = (iso: string): string => todayAtFacility(new Date(iso))
